import React from 'react';
import { X, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ModalShell from '../shared/ModalShell';

const DAY_LABELS = {
  0: 'Today',
  1: 'Tomorrow (1 day)',
  2: '2 days away',
  3: '3 days away',
  4: '4 days away',
  5: '5 days away',
};

const formatEventDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const EventReminderModal = ({ isOpen, onClose, bookingsByDays, formatDate }) => {
  const navigate = useNavigate();
  const format = typeof formatDate === 'function' ? formatDate : formatEventDate;

  const totalCount = Object.values(bookingsByDays || {}).flat().length;
  const open = Boolean(isOpen && totalCount > 0);

  const handleViewBookings = () => {
    onClose();
    navigate('/vendor/bookings');
  };

  return (
    <ModalShell
      isOpen={open}
      onClose={onClose}
      labelledBy="event-reminder-title"
      panelClassName="max-w-lg"
    >
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-lg w-full border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-12 h-12 bg-sky-50 dark:bg-sky-950/40 border border-sky-100 dark:border-sky-800/50 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-sky-600 dark:text-sky-400" />
              </div>
              <div>
                <span className="inline-block px-2.5 py-0.5 bg-sky-100 dark:bg-sky-950/50 text-sky-800 dark:text-sky-300 text-xs font-medium rounded-full mb-1">
                  Event Reminder
                </span>
                <h3 id="event-reminder-title" className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                  {totalCount} upcoming event{totalCount !== 1 ? 's' : ''} (5th → event day)
                </h3>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="border-t border-gray-200 dark:border-gray-800 pt-4">
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
              Your events from 5 days away through the event day:
            </p>
            <div className="space-y-4 max-h-64 overflow-y-auto">
              {[5, 4, 3, 2, 1, 0].map((days) => {
                const bookings = bookingsByDays[days] || [];
                if (bookings.length === 0) return null;
                return (
                  <div key={days} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-sky-700 dark:text-sky-400 uppercase tracking-wide">
                        {DAY_LABELS[days]}
                      </span>
                      {bookings[0]?.event_date && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          ({format(bookings[0].event_date)})
                        </span>
                      )}
                    </div>
                    <ul className="space-y-1.5">
                      {bookings.map((booking) => (
                        <li
                          key={booking.id}
                          className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-950/50 border border-gray-100 dark:border-gray-800 rounded-xl text-sm hover:border-sky-200 dark:hover:border-sky-800/50 transition-colors"
                        >
                          <div>
                            <span className="font-medium text-gray-900 dark:text-gray-50">
                              {booking.booking_reference || `#${booking.id?.substring(0, 8)}`}
                            </span>
                            {booking.client?.name && (
                              <span className="text-gray-600 dark:text-gray-400 ml-2">
                                – {booking.client.name}
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              onClose();
                              navigate(`/vendor/bookings/${booking.id}`);
                            }}
                            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium text-xs"
                          >
                            View
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 !rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              Dismiss
            </button>
            <button
              type="button"
              onClick={handleViewBookings}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 !rounded-xl transition-colors shadow-sm"
            >
              View All Bookings
            </button>
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

export default EventReminderModal;
